import {catchError, shareReplay, tap, map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, noop, Subscription} from 'rxjs';

export interface ModeData {
  edit: boolean;
}

type WorkerCallback = () => Promise<any> | any;

@Injectable()
export class ModeDataService {
  private currentModeSubject: BehaviorSubject<ModeData> = new BehaviorSubject({edit: false});
  public currentMode$: Observable<ModeData> = this.currentModeSubject.asObservable();
  private modeHooks: {edit: [WorkerCallback]; view: [WorkerCallback]; any: [WorkerCallback]} = {
    edit: [noop],
    view: [noop],
    any: [noop]
  };
  private currentModeSubscription: Subscription;
  private interruptHooks: boolean;
  public isEditMode$: Observable<boolean> = this.currentMode$.pipe(
    map(mode => mode.edit),
    shareReplay()
  );
  public isViewMode$: Observable<boolean> = this.currentMode$.pipe(
    map(mode => !mode.edit),
    shareReplay()
  );

  public initFlag: boolean;

  constructor() {}

  public isEditMode() {
    return this.currentModeSubject.getValue().edit;
  }

  public isViewMode() {
    return !this.isEditMode();
  }

  /**
   * Use this method to trigger respective mode callbacks
   * @param {boolean} mode
   */
  public modeSwitch(mode: boolean): void {
    this.currentModeSubject.next({edit: mode});
  }

  public onEdit(cb: WorkerCallback | Array<WorkerCallback>): this {
    if (Array.isArray(cb)) {
      this.modeHooks.edit.push(...cb);
    } else {
      this.modeHooks.edit.push(cb);
    }

    return this;
  }

  public onView(cb: WorkerCallback | Array<WorkerCallback>): this {
    if (Array.isArray(cb)) {
      this.modeHooks.view.push(...cb);
    } else {
      this.modeHooks.view.push(cb);
    }

    return this;
  }

  public onAny(cb: WorkerCallback | Array<WorkerCallback>): this {
    if (Array.isArray(cb)) {
      this.modeHooks.any.push(...cb);
    } else {
      this.modeHooks.any.push(cb);
    }

    return this;
  }

  public subscribe(): void {
    if (this.currentModeSubscription && !this.currentModeSubscription.closed) {
      return;
    }

    this.interruptHooks = false;
    this.currentModeSubscription = this.currentMode$
      .pipe(
        tap(mode => {
          this.worker(mode);
        }),
        catchError((err, obs) => {
          return obs;
        })
      )
      .subscribe();
  }

  public init(auto?: boolean): this {
    if (!auto) {
      this.initFlag = true;
    }

    this.unsubscribe(true); // clear any leftover subs
    this.modeHooks.edit = [noop];
    this.modeHooks.view = [noop];
    this.modeHooks.any = [noop];

    return this;
  }

  private worker(mode: ModeData): void {
    let workQue = [].concat(this.modeHooks.any);

    if (mode.edit === true) {
      workQue = workQue.concat(this.modeHooks.edit);
    } else {
      workQue = workQue.concat(this.modeHooks.view);
    }

    this.doWork(workQue);
  }

  private doWork(que) {
    if (this.interruptHooks || que.length === 0) {
      return;
    }

    const work = que.shift();

    return Promise.resolve(work())
      .then(() => this.doWork(que))
      .catch(err => {
        this.unsubscribe(true);
      });
  }

  public unsubscribe(interruptHooks?: boolean): void {
    this.interruptHooks = interruptHooks;

    if (
      this.currentModeSubscription &&
      typeof this.currentModeSubscription.unsubscribe === 'function'
    ) {
      this.currentModeSubscription.unsubscribe();
    }
  }
}
